import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { PaginationControl } from "react-bootstrap-pagination-control";
import axios from "axios";
import Swal from "sweetalert2";
import Spinner from "react-bootstrap/Spinner";
import {
  ExcelExport,
  ExcelExportColumn,
} from "@progress/kendo-react-excel-export";

import BASE_API_URL from "../../../../api/api";
import apiInstance from "../../../../Utils/api";
import { store } from "../../../../redux/store";

// Constants
const DATE_OPTIONS = {
  today: new Date(),
  weekAgo: new Date(new Date().setDate(new Date().getDate() - 7)),
};

const LOG_TYPES = {
  HO: [
    "Stock Update",
    "Purchase",
    "Stock Transfer",
    "Issue To HO",
    "K nas Sale",
  ],
  BRANCH: [
    "Usage",
    "Stock Transfer",
    "Request Recieved",
    "Stock Update",
    "Medicine Sale",
    "Issue To Clinic",
    "Voucher Cancel"
  ],
};

const ITEM_CATEGORIES = {
  MEDICINE: "Medicine",
  PROCEDURE_MEDICINE: "Procedure Medicine",
  PROCEDURE_ACCESSORY: "Procedure Accessory",
  MACHINE: "Machine",
};

const API_ENDPOINTS = {
  [ITEM_CATEGORIES.MACHINE]: "fixed-assets",
  [ITEM_CATEGORIES.MEDICINE]: "medicine-lists",
  [ITEM_CATEGORIES.PROCEDURE_MEDICINE]: "procedure-medicines",
  [ITEM_CATEGORIES.PROCEDURE_ACCESSORY]: "procedure-accessories",
};

const ITEMS_API_ENDPOINTS = {
  [ITEM_CATEGORIES.MEDICINE]: "medicine-items",
  [ITEM_CATEGORIES.PROCEDURE_MEDICINE]: "procedure-items",
  [ITEM_CATEGORIES.PROCEDURE_ACCESSORY]: "accessory-items",
};

const ITEM_QUERY_KEY = {
  [ITEM_CATEGORIES.MEDICINE]: "relatedMedicineItems",
  [ITEM_CATEGORIES.PROCEDURE_MEDICINE]: "relatedProcedureItems",
  [ITEM_CATEGORIES.PROCEDURE_ACCESSORY]: "relatedAccessoryItems",
};

// Utility functions
const formatDate = (date) => date.toISOString().split("T")[0];
const addDays = (date, days) => {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() + days);
  return newDate;
};

const formatExportData = (data) => {
  return data.map((element, index) => ({
    ...element,
    no: index + 1,
    date: element.date ? element.date.split("T")[0] : "",
    time: element.date ? element.date.split("T")[1]?.slice(0, 5) : "",
    code:
      element?.relatedTreatmentVoucher?.code ||
      element?.relatedStockRequest?.branchRequestCode ||
      element?.relatedTreatmentSelection?.[0]?.code ||
      "-",
    name:
      element.relatedProcedureItems?.[0]?.procedureItemName ||
      element.relatedAccessoryItems?.[0]?.accessoryItemName ||
      element.relatedMachine?.[0]?.name ||
      element.relatedMedicineItems?.[0]?.medicineItemName ||
      element.relatedGeneralItems?.[0]?.generalItemName ||
      element?.relatedStock?.relatedMedicineItems?.[0]?.medicineItemName ||
      element?.relatedStock?.relatedProcedureItems?.[0]?.procedureItemName ||
      element?.relatedStock?.relatedAccessoryItems?.[0]?.accessoryItemName ||
      "-",
    itemType: element.itemType || "-",
    relatedTreatment:
      element.relatedTreatmentSelection?.[0]?.relatedTreatment?.[0]?.name ||
      "-",
    branch:
      element.relatedBranch?.[0]?.name ||
      element.relatedBranch?.branchName ||
      "HO",
    createdBy: element.createdBy[0] ? element.createdBy[0]?.givenName : "-",
    qty:
      element.actualQty ||
      (element.currentQty && element.finalQty
        ? Math.round(element.finalQty - element.currentQty)
        : "-"),
    currentQty: element.currentQty
      ? element?.qtyType === "toQty"
        ? Math.round(element.currentQty)
        : element.currentQty % 1 === 0
          ? element.currentQty
          : element.currentQty.toFixed(
              element.currentQty.toString().split(".")[1].length > 2 ? 2 : 0,
            )
      : "0",
    actualQty:
      element.actualQty ||
      (element.currentQty && element.finalQty
        ? Math.round(element.finalQty - element.currentQty)
        : "-"),
  }));
};

export default function StockList() {
  // State Management
  const [loading, setLoading] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);
  const [stockList, setStockList] = useState([]);
  const [filteredStockList, setFilteredStockList] = useState([]);
  const [metaData, setMetaData] = useState({
    count: 0,
    page: 1,
    per_page: 10,
    total_Page: 1,
  });
  const [branchLists, setBranchLists] = useState([]);
  const [itemList, setItemList] = useState([]);
  const [items, setItems] = useState([]);

  // Filter States
  const [filters, setFilters] = useState({
    fromDate: formatDate(DATE_OPTIONS.weekAgo),
    toDate: formatDate(DATE_OPTIONS.today),
    branchId: "HO",
    type: "Stock Update",
    category: "",
    itemId: "",
    subItemId: "",
  });

  // UI States
  const [showExport, setShowExport] = useState(false);
  const [showSubItems, setShowSubItems] = useState(true);
  const [isLoadingItems, setIsLoadingItems] = useState(false);

  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Refs
  const exportRef = useRef(null);
  const isInitialMount = useRef(true);
  const exportAbortController = useRef(null);

  const defaultParams = useMemo(() => {
    return {
      page: 1,
      limit: rowsPerPage,
      ...(filters.fromDate &&
        filters.toDate && {
          start: filters.fromDate,
          end: formatDate(addDays(new Date(filters.toDate), 1)),
        }),
      ...(filters.branchId && { branch: filters.branchId }),
      ...(filters.type && { type: filters.type }),
      ...(filters.subItemId && { id: filters.subItemId }),
      ...(filters.itemId &&
        filters.category === ITEM_CATEGORIES.MACHINE && {
          id: filters.itemId,
        }),
      ...(filters.category &&
        filters.subItemId && {
          [ITEM_QUERY_KEY[filters.category]]: filters.subItemId,
        }),
    };
  }, [filters, rowsPerPage]);

  // API Setup
  const token = store.getState().auth.user.token;
  const api = useMemo(
    () =>
      apiInstance,
    [token],
  );

  // Helper Functions
  const showAlert = useCallback((title, message, icon = "error") => {
    Swal.fire({
      title,
      text: message,
      icon,
      confirmButtonText: "OK",
    });
  }, []);

  const updateFilter = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Data Fetching Functions
  const fetchStockLogs = useCallback(
    async (params = {}, resetPage = true) => {
      setLoading(true);
      try {
        const pageToUse = resetPage ? 1 : currentPage;

        const response = await api.get("logs/filter", {
          params: {
            page: pageToUse,
            limit: rowsPerPage,
            ...params,
          },
        });

        const data = response?.data?.data || [];
        const meta = response?.data?.meta_data || {
          count: data.length,
          page: pageToUse,
          per_page: rowsPerPage,
          total_Page: Math.ceil(data.length / rowsPerPage) || 1,
        };

        setStockList(data);
        setFilteredStockList(data);
        setMetaData(meta);

        if (resetPage) {
          setCurrentPage(1);
        }

        setShowExport(true);
      } catch (error) {
        showAlert(
          "Error",
          error.response?.data?.message || "Failed to fetch data",
        );
        console.log(error);
      } finally {
        setLoading(false);
      }
    },
    [api, showAlert, currentPage, rowsPerPage],
  );

  const fetchBranches = useCallback(async () => {
    try {
      const response = await api.get("branches");
      setBranchLists(response.data.list || []);
    } catch (error) {
      console.error("Failed to fetch branches:", error);
    }
  }, [api]);

  const fetchItemList = useCallback(
    async (category) => {
      try {
        const endpoint = API_ENDPOINTS[category];
        if (!endpoint) return;

        const response = await api.get(endpoint);
        setItemList(response.data.list || []);
        updateFilter("itemId", "");
        updateFilter("subItemId", "");
      } catch (error) {
        showAlert(
          "Error",
          error.response?.data?.message || "Failed to fetch items",
        );
      }
    },
    [api, showAlert, updateFilter, filters.category],
  );

  const fetchSubItems = useCallback(
    async (itemId) => {
      if (!itemId) return;

      setIsLoadingItems(true);
      try {
        const endpoint = ITEMS_API_ENDPOINTS[filters.category];
        if (!endpoint) return;

        const response = await api.get(`${endpoint}/${itemId}`);
        setItems(response.data.data || []);
      } catch (error) {
        showAlert(
          "Error",
          error.response?.data?.message || "Failed to fetch sub-items",
        );
      } finally {
        setIsLoadingItems(false);
      }
    },
    [api, showAlert, filters.itemId, filters.category],
  );

  // Filter Handlers
  const handleFilter = useCallback(async () => {
    const params = {};

    if (filters.fromDate && filters.toDate) {
      const nextDay = addDays(new Date(filters.toDate), 1);
      params.start = filters.fromDate;
      params.end = formatDate(nextDay);
    }

    if (filters.branchId) {
      params.branch = filters.branchId;
    }

    if (filters.type) {
      params.type = filters.type;
    }

    if (filters.category && filters.subItemId) {
      params[ITEM_QUERY_KEY[filters.category]] = filters.subItemId;
    }

    await fetchStockLogs(params, true);
  }, [filters, fetchStockLogs]);

  const handleBranchChange = useCallback(
    (branchId) => {
      updateFilter("branchId", branchId);

      let type = "";
      if (branchId === "HO") {
        updateFilter("type", "Stock Update");
        type = "Stock Update";
      } else {
        updateFilter("type", "");
      }
      const params = {
        ...defaultParams,
        type,
        branch: branchId,
      };
      fetchStockLogs(params, true);
    },
    [filters, fetchStockLogs, updateFilter],
  );

  const handleCategoryChange = useCallback(
    (category) => {
      updateFilter("category", category);
      updateFilter("itemId", "");
      updateFilter("subItemId", "");

      if (category === ITEM_CATEGORIES.MACHINE) {
        updateFilter("type", "");
      }
      setShowSubItems(true);

      fetchItemList(category);
    },
    [fetchItemList, updateFilter],
  );

  const handleItemChange = useCallback(
    async (itemId) => {
      updateFilter("itemId", itemId);

      if (filters.category === ITEM_CATEGORIES.MACHINE) {
        try {
          setLoading(true);
          const response = await api.get("logs/filter", {
            params: {
              id: itemId,
              page: 1,
              limit: rowsPerPage,
            },
          });

          const data = response?.data?.data || [];
          const meta = response?.data?.meta_data || {
            count: data.length,
            page: 1,
            per_page: rowsPerPage,
            total_Page: Math.ceil(data.length / rowsPerPage) || 1,
          };

          setStockList(data);
          setFilteredStockList(data);
          setMetaData(meta);
          setCurrentPage(1);
        } catch (error) {
          showAlert(
            "Error",
            error.response?.data?.message || "Failed to filter items",
          );
        } finally {
          setLoading(false);
        }
      } else {
        await fetchSubItems(itemId);
      }
    },
    [
      filters.category,
      api,
      fetchSubItems,
      showAlert,
      updateFilter,
      rowsPerPage,
    ],
  );

  const handleSubItemChange = useCallback(
    async (event) => {
      const subItemId = event.target.value;
      updateFilter("subItemId", subItemId);
    },
    [api, showAlert, updateFilter, rowsPerPage],
  );

  // Export Handler
  const handleExport = useCallback(() => {
    if (!exportRef.current) return;

    exportAbortController.current = new AbortController();

    const exportFilters = {
      ...(filters.fromDate &&
        filters.toDate && {
          start: filters.fromDate,
          end: formatDate(addDays(new Date(filters.toDate), 1)),
        }),
      ...(filters.branchId && { branch: filters.branchId }),
      ...(filters.type && { type: filters.type }),
      ...(filters.subItemId && { id: filters.subItemId }),
      ...(filters.itemId &&
        filters.category === ITEM_CATEGORIES.MACHINE && { id: filters.itemId }),
      ...(filters.category &&
        filters.subItemId && {
          [ITEM_QUERY_KEY[filters.category]]: filters.subItemId,
        }),
      limit: 10000,
    };

    setExcelLoading(true);

    api
      .get("logs/filter", {
        params: exportFilters,
        signal: exportAbortController.current.signal,
      })
      .then((response) => {
        const exportData = formatExportData(response?.data?.data || []);
        exportRef.current.save(exportData);
      })
      .catch((error) => {
        if (error.name === "CanceledError" || error.code === "ERR_CANCELED") {
          console.log("Export cancelled");
        } else {
          showAlert("Error", "Failed to export data", "error");
        }
      })
      .finally(() => {
        setExcelLoading(false);
        exportAbortController.current = null;
      });
  }, [filters, api, showAlert]);

  // Handle page change
  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
  }, []);

  // Fetch data when page changes
  useEffect(() => {
    console.log("useEffect first time");
    if (!isInitialMount.current) {
      console.log("useEffect inside");
      const params = {
        page: currentPage,
        limit: rowsPerPage,
        ...(filters.fromDate &&
          filters.toDate && {
            start: filters.fromDate,
            end: formatDate(addDays(new Date(filters.toDate), 1)),
          }),
        ...(filters.branchId && { branch: filters.branchId }),
        ...(filters.type && { type: filters.type }),
        ...(filters.subItemId && { id: filters.subItemId }),
        ...(filters.itemId &&
          filters.category === ITEM_CATEGORIES.MACHINE && {
            id: filters.itemId,
          }),
        ...(filters.category &&
          filters.subItemId && {
            [ITEM_QUERY_KEY[filters.category]]: filters.subItemId,
          }),
      };

      fetchStockLogs(params, false);
    } else {
      isInitialMount.current = false;
    }
  }, [currentPage, rowsPerPage]);

  // Initial data fetch
  useEffect(() => {
    fetchBranches();
    handleFilter();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Get available log types based on branch
  const availableLogTypes =
    filters.branchId === "HO" ? LOG_TYPES.HO : LOG_TYPES.BRANCH;

  // Styles
  // const scrollStyle = { height: "550px", overflowY: "scroll" };

  return (
    <div className="wrapper">

      <div className="content-wrapper">
        {/* Header */}
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-12">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item active fs-5 text-bold">
                    Log List
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="row">
          <div className="col-md-12">
            <div className="row ml-2">
              {/* Date Filters */}
              <div className="form-group col-md-2">
                <label>From</label>
                <input
                  type="date"
                  className="form-control"
                  value={filters.fromDate}
                  onChange={(e) => updateFilter("fromDate", e.target.value)}
                />
              </div>
              <div className="form-group col-md-2">
                <label>To</label>
                <input
                  type="date"
                  className="form-control"
                  value={filters.toDate}
                  onChange={(e) => updateFilter("toDate", e.target.value)}
                />
              </div>

              {/* Type Filter */}
              <div className="form-group col-md-2">
                <label>Log Type</label>
                <select
                  className="custom-select mt-2 border-info"
                  value={filters.type}
                  onChange={(e) => updateFilter("type", e.target.value)}
                >
                  <option value="">All Types</option>
                  {availableLogTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              {/* Filter Button */}
              <div className="col-md-1 p-1">
                <button
                  className="btn btn-sm btn-primary form-control"
                  style={{ marginTop: "33px" }}
                  onClick={handleFilter}
                  disabled={loading}
                >
                  Filter
                </button>
              </div>

              {/* Branch Filter */}
              <div className="form-group col-md-2">
                <label className="">Branch</label>
                <select
                  className="custom-select mt-2 border-info"
                  value={filters.branchId}
                  onChange={(e) => handleBranchChange(e.target.value)}
                >
                  <option value="HO">HO</option>
                  {branchLists.map((branch) => (
                    <option key={branch._id} value={branch._id}>
                      {branch?.name}
                    </option>
                  ))}
                </select>
              </div>

              {showExport && (
                <div
                  className="form-group col-md-3"
                  style={{ marginTop: "2.4em" }}
                >
                  <button
                    className="btn btn-md btn-success"
                    onClick={handleExport}
                    disabled={excelLoading}
                  >
                    {excelLoading ? (
                      <>
                        <div
                          className="spinner-border spinner-border-sm"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </>
                    ) : (
                      "Export"
                    )}
                  </button>
                  {excelLoading && (
                    <span
                      className="text-danger ms-2"
                      style={{ cursor: "pointer" }}
                      onClick={() => exportAbortController.current?.abort()}
                    >
                      Cancel Export
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="row">
          <div className="col-12">
            <div className="card mx-3">
              {/* Item Filters Card Header */}
              {showExport && (
                <div className="card-header">
                  <div className="d-flex justify-content-between align-items-end">
                    <div className="w-75">
                      <div className="row">
                        <div className="form-group col-md-2 mt-2">
                          <label htmlFor="">Category</label>
                          <select
                            className="custom-select border-info"
                            value={filters.category}
                            onChange={(e) =>
                              handleCategoryChange(e.target.value)
                            }
                          >
                            <option value="">All</option>
                            {Object.values(ITEM_CATEGORIES).map((category) => (
                              <option key={category} value={category}>
                                {category}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="form-group col-md-2 mt-2">
                          <label htmlFor="">All</label>
                          <select
                            className="custom-select border-info"
                            value={filters.itemId}
                            onChange={(e) => handleItemChange(e.target.value)}
                            disabled={!filters.category || loading}
                          >
                            <option value="">All</option>
                            {itemList.map((item) => (
                              <option key={item._id} value={item._id}>
                                {item?.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        {showSubItems &&
                          filters.category !== ITEM_CATEGORIES.MACHINE && (
                            <div className="form-group col-md-2 mt-2">
                              <label htmlFor="">Item</label>
                              <select
                                className="custom-select border-info"
                                value={filters.subItemId}
                                onChange={handleSubItemChange}
                                disabled={!filters.itemId || loading}
                              >
                                <option value="">All</option>
                                {items.map((item) => (
                                  <option key={item._id} value={item._id}>
                                    {item.procedureItemName ||
                                      item.accessoryItemName ||
                                      item.medicineItemName}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}

                        {isLoadingItems && (
                          <div className="form-group col-md-2 mt-3">
                            <span className="text-lg">Loading ...</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="me-2">
                      <span className="me-3">
                        {metaData.count === 0 ? (
                          <>Showing 0 of 0</>
                        ) : (
                          <>
                            Showing {(currentPage - 1) * rowsPerPage + 1}
                            {" - "}
                            {Math.min(
                              currentPage * rowsPerPage,
                              metaData.count,
                            )}
                            {" of "}
                            <strong>{metaData.count}</strong>
                          </>
                        )}
                      </span>

                      <select
                        value={rowsPerPage}
                        onChange={(e) => {
                          setRowsPerPage(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                        className="form-select d-inline-block"
                        style={{ width: "90px", marginLeft: "10px" }}
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                      <span className="ms-2">rows</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Excel Export Component */}
              <ExcelExport ref={exportRef}>
                <ExcelExportColumn
                  field="no"
                  title="No"
                  locked={true}
                  width={30}
                />
                <ExcelExportColumn field="date" title="Date" width={250} />
                <ExcelExportColumn field="time" title="Time" width={250} />
                <ExcelExportColumn
                  field="code"
                  title="Voucher Code"
                  width={200}
                />
                <ExcelExportColumn field="name" title="Item Name" width={350} />
                <ExcelExportColumn
                  field="itemType"
                  title="Item Type"
                  width={150}
                />
                <ExcelExportColumn
                  field="relatedTreatment"
                  title="Related Treatments"
                  width={150}
                />
                <ExcelExportColumn
                  field="type"
                  title="Usage Type"
                  width={150}
                />
                <ExcelExportColumn
                  field="currentQty"
                  title="From Qty"
                  width={150}
                />
                <ExcelExportColumn
                  field="qty"
                  title="Changed Qty"
                  width={150}
                />
                <ExcelExportColumn
                  field="finalQty"
                  title="To Qty"
                  width={150}
                />
                <ExcelExportColumn field="branch" title="Branch" width={150} />
                <ExcelExportColumn
                  field="createdBy"
                  title="Created By"
                  width={150}
                />
              </ExcelExport>

              {/* Table */}
              <div className="card-body overflow-x-auto">
                <table id="example1" className="table">
                  <thead className="sticky-top bg-light">
                    <tr>
                      <th>No</th>
                      <th>Date</th>
                      <th>Time</th>
                      <th>Code</th>
                      <th>Item Name</th>
                      <th>Item Type</th>
                      <th>Related Treatment</th>
                      <th>Log Type</th>
                      <th>Qty Type</th>
                      <th>From Qty</th>
                      <th>Changed QTY</th>
                      <th>To QTY</th>
                      <th>Branch</th>
                      <th>Created By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!loading &&
                      stockList.map((item, index) => (
                        <tr key={item._id}>
                          <td>{(currentPage - 1) * rowsPerPage + index + 1}</td>
                          <td>{item.date ? item.date.split("T")[0] : ""}</td>
                          <td>
                            {item.date
                              ? new Date(item.date).toLocaleTimeString(
                                  "en-GB",
                                  {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    hour12: false,
                                  },
                                )
                              : ""}
                          </td>
                          <td>
                            {item?.relatedTreatmentVoucher?.code ||
                              item?.relatedStockRequest?.branchRequestCode ||
                              item.relatedTreatmentSelection?.[0]?.code ||
                              "-"}
                          </td>
                          <td>
                            {item.relatedProcedureItems?.[0]
                              ?.procedureItemName ||
                              item.relatedAccessoryItems?.[0]
                                ?.accessoryItemName ||
                              item.relatedMachine?.[0]?.name ||
                              item.relatedMedicineItems?.[0]
                                ?.medicineItemName ||
                              item.relatedGeneralItems?.[0]?.generalItemName ||
                              item?.relatedStock?.relatedMedicineItems?.[0]
                                ?.medicineItemName ||
                              item?.relatedStock?.relatedProcedureItems?.[0]
                                ?.procedureItemName ||
                              item?.relatedStock?.relatedAccessoryItems?.[0]
                                ?.accessoryItemName ||
                              "-"}
                          </td>
                          <td className="text-capitalize">
                            {item?.itemType || "-"}
                          </td>
                          <td>
                            {item.relatedTreatmentSelection?.[0]
                              ?.relatedTreatment?.[0]?.name || "-"}
                          </td>
                          <td>{item?.type || "-"}</td>
                          <td>{item?.qtyType || "-"}</td>
                          <td>
                            {item.currentQty
                              ? item?.qtyType === "toQty"
                                ? Math.round(item.currentQty)
                                : item.currentQty % 1 === 0
                                  ? item.currentQty
                                  : item.currentQty.toFixed(
                                      item.currentQty.toString().split(".")[1]
                                        .length > 2
                                        ? 2
                                        : 0,
                                    )
                              : "0"}
                          </td>
                          <td
                            className={
                              item?.actualQty < 0 ||
                              item?.currentQty > item?.finalQty
                                ? "text-danger"
                                : "text-success"
                            }
                          >
                            {item.actualQty ||
                              (item.currentQty && item.finalQty
                                ? Math.round(item.finalQty - item.currentQty)
                                : "-")}
                          </td>
                          <td>
                            {item.finalQty
                              ? item?.qtyType === "toQty"
                                ? Math.round(item.finalQty)
                                : item.finalQty % 1 === 0
                                  ? item.finalQty
                                  : item.finalQty.toFixed(
                                      item.finalQty.toString().split(".")[1]
                                        .length > 2
                                        ? 2
                                        : 0,
                                    )
                              : "0"}
                          </td>

                          <td>
                            {item.relatedBranch?.[0]?.name ||
                              item.relatedBranch?.branchName ||
                              "HO"}
                          </td>
                          <td>{item.createdBy?.[0]?.givenName || "---"}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>

                {/* Loading indicator in table area */}
                {loading && (
                  <div
                    className="d-flex justify-content-center align-items-center"
                    style={{ minHeight: "300px", width: "100%" }}
                  >
                    <div className="text-center">
                      <Spinner
                        animation="border"
                        variant="primary"
                        style={{ width: "3rem", height: "3rem" }}
                      />
                      <p className="mt-2 text-muted">Loading data...</p>
                    </div>
                  </div>
                )}

                {!loading && stockList.length === 0 && (
                  <div className="text-center py-5">
                    <span className="text-muted">No data available</span>
                  </div>
                )}
              </div>

              {/* Pagination */}
              {!loading && (
                <div className="d-flex justify-content-end align-items-center my-4 px-3">
                  {/* LEFT SIDE */}
                  <div className="me-2">
                    <span className="me-3">
                      {metaData.count === 0 ? (
                        <>Showing 0 of 0</>
                      ) : (
                        <>
                          Showing {(currentPage - 1) * rowsPerPage + 1}
                          {" - "}
                          {Math.min(currentPage * rowsPerPage, metaData.count)}
                          {" of "}
                          <strong>{metaData.count}</strong>
                        </>
                      )}
                    </span>

                    <select
                      value={rowsPerPage}
                      onChange={(e) => {
                        setRowsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="form-select d-inline-block"
                      style={{ width: "90px", marginLeft: "10px" }}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span className="ms-2">rows</span>
                  </div>

                  {/* RIGHT SIDE */}
                  {metaData.total_Page > 1 && (
                    <div className="pt-3">
                      <PaginationControl
                        page={currentPage}
                        between={4}
                        total={metaData.count}
                        limit={rowsPerPage}
                        changePage={handlePageChange}
                        ellipsis={1}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="main-footer">
        <strong>
          Copyright &copy; 2017-2020{" "}
          <a href="http://www.kwintechnologies.com">K-win Technology</a>.
        </strong>
        All rights reserved.
      </footer>
    </div>
  );
}
